import React, { useState, useEffect, useCallback } from 'react';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import { useAuth } from '../../../shared/hooks/useAuth';
import api from '../../../shared/utils/api';
import ErrorPopup from '../../../shared/components/ErrorPopup';
import WarningPopup from '../../../shared/components/WarningPopup';
import AccountPeriodBalanceItemPopup from '../../customers/components/AccountPeriodBalanceItemPopup';

interface AccountPeriodBalanceRecord {
  Oid: string;
  CustomerId: string;
  YearType: number;
  ActiveAmount: number | null;
  PassiveAmount: number | null;
  CustomerName: string | null;
  CustomerPin: string | null;
}

interface AccountPeriodBalanceItemRecord {
  Oid: string;
  AccountPeriodBalanceOid: string;
  AccountOid: string | null;
  ActiveAmount: number | null;
  PassiveAmount: number | null;
  BusinessCustomerOid: string;
  CustomerName: string | null;
  CustomerPin: string | null;
  PeriodBalanceCustomerId?: string;
  YearType?: number;
}

interface AccountPeriodBalanceResponse {
  success: boolean;
  message: string;
  data: AccountPeriodBalanceRecord[];
  total: number;
  filters: {
    appliedFilters: {
      CustomerOid: boolean;
      yearType: boolean;
    };
  };
}

interface CustomerOption {
  customerOid: string;
  customerID: string;
  customerName: string;
}

const AccountPeriodBalance: React.FC = () => {
  const { userAuth } = useAuth();
  const [balanceData, setBalanceData] = useState<AccountPeriodBalanceRecord[]>([]);
  const [itemData, setItemData] = useState<AccountPeriodBalanceItemRecord[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Debug logging for production issues
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 AccountPeriodBalance component mounted');
      console.log('📊 userAuth:', userAuth);
    }
  }, [userAuth]);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [selectedYearType, setSelectedYearType] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('Бүгд');
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [selectedBalanceOid, setSelectedBalanceOid] = useState<string | undefined>(undefined);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editingRowData, setEditingRowData] = useState<Partial<AccountPeriodBalanceItemRecord> | null>(null);
  const [newRow, setNewRow] = useState<Partial<AccountPeriodBalanceItemRecord> | null>(null);
  const [accountPeriodBalances, setAccountPeriodBalances] = useState<AccountPeriodBalanceRecord[]>([]);
  const [accounts, setAccounts] = useState<Array<{ Oid: string; Code: string; Name: string }>>([]);
  const [businessCustomers, setBusinessCustomers] = useState<Array<{ Code: string; Name: string }>>([]);

  // Fetch customers list for dropdown
  const fetchCustomers = useCallback(async () => {
    try {
      const accountantOid = userAuth.accountantOid || localStorage.getItem('accountantOid');
      
      if (!accountantOid) {
        return;
      }

      const queryParams = new URLSearchParams({
        accountantOid: accountantOid
      });
      const apiUrl = `/api/customerslist?${queryParams.toString()}`;
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        const customerOptions: CustomerOption[] = result.data.map((customer: any) => ({
          customerOid: customer.Oid,
          customerID: customer.CustomerID,
          customerName: customer.CustomerName
        }));
        setCustomers(customerOptions);
      }
    } catch (error) {
      console.error('❌ Error fetching customers:', error);
    }
  }, [userAuth.accountantOid]);

  // Fetch accounts list
  const fetchAccounts = useCallback(async () => {
    try {
      const result = await api.get('/api/account?isActive=true');
      if (result?.success && result?.data) {
        setAccounts(result.data.map((item: any) => ({
          Oid: item.Oid,
          Code: item.Code || '',
          Name: item.Name || ''
        })));
      }
    } catch (error) {
      console.error('❌ Error fetching accounts:', error);
    }
  }, []);

  // Fetch business customers list
  const fetchBusinessCustomers = useCallback(async () => {
    try {
      const result = await api.get('/api/business-customer-list');
      if (result?.success && result?.data) {
        setBusinessCustomers(result.data.map((item: any) => ({
          Code: item.Code || '',
          Name: item.Name || ''
        })));
      }
    } catch (error) {
      console.error('❌ Error fetching business customers:', error);
    }
  }, []);

  // Fetch AccountPeriodBalance data
  const fetchAccountPeriodBalance = useCallback(async () => {
    try {
      setLoading(true);
      if (process.env.NODE_ENV === 'development') {
        console.log('🔥 AccountPeriodBalance: Starting to fetch balance data...');
        console.log('🔥 Selected yearType:', selectedYearType);
        console.log('🔥 Selected customer:', selectedCustomer);
      }
      
      const queryParams = new URLSearchParams();
      
      // CustomerOid байхгүй / "Бүгд" бол all
      if (selectedCustomer && selectedCustomer !== 'Бүгд') {
        queryParams.append('CustomerOid', selectedCustomer);
      }
      
      // Add yearType filter if selected
      if (selectedYearType) {
        queryParams.append('yearType', selectedYearType);
      }
      
      const apiUrl = `/api/account-period-balance${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🌐 API URL:', apiUrl);
      }
      
      const result: AccountPeriodBalanceResponse = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Fetched ${result.data.length} balance records`);
        }
        
        setBalanceData(result.data);
        setAccountPeriodBalances(result.data);
        
        // Show warning if no data found
        if (result.data.length === 0) {
          setWarningMessage('AccountPeriodBalance мэдээлэл олдсонгүй');
          setShowWarningAlert(true);
        } else {
          setShowWarningAlert(false);
        }
      } else {
        setErrorMessage('AccountPeriodBalance мэдээлэл авахад алдаа гарлаа');
        setShowErrorPopup(true);
        setBalanceData([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching AccountPeriodBalance:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      let errorMessage = 'AccountPeriodBalance мэдээлэл авахад алдаа гарлаа';
      
      if (error.response?.status === 404) {
        errorMessage = 'API endpoint олдсонгүй. Сервертэй холбогдож чадсангүй.';
        setWarningMessage('AccountPeriodBalance мэдээлэл олдсонгүй');
        setShowWarningAlert(true);
      } else if (error.response?.status === 401) {
        errorMessage = 'Нэвтрэх эрх шаардлагатай. Дахин нэвтэрнэ үү.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Серверийн алдаа. Дахин оролдоно уу.';
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        errorMessage = 'Сүлжээний алдаа. Сервер ажиллаж байгаа эсэхийг шалгана уу.';
      }
      
      setErrorMessage(errorMessage);
      setShowErrorPopup(true);
      setBalanceData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYearType, selectedCustomer]);

  // Button handlers
  const handleToggleSearch = () => {
    setShowColumnSearch(prev => !prev);
  };

  const handleColumnSettings = () => {
    setShowColumnSettings(prev => !prev);
  };

  const handleRefresh = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Сэргээх товч дарагдаж, өгөгдөл дахин ачаалж байна...');
    }
    fetchAccountPeriodBalance();
  };

  const handleAdd = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('➕ Нэмэх товч дарагдаж, шинэ AccountPeriodBalanceItem нэмэх...');
    }
    setShowAddPopup(true);
  };

  const handleAddSuccess = () => {
    // Refresh items data after successful add
    fetchAccountPeriodBalanceItems();
  };

  // Fetch AccountPeriodBalanceItems data
  const fetchAccountPeriodBalanceItems = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams();
      
      if (selectedCustomer !== 'Бүгд') {
        queryParams.append('businessCustomerOid', selectedCustomer);
      }
      
      if (selectedYearType) {
        // Find AccountPeriodBalanceOid from selectedYearType
        const balance = accountPeriodBalances.find(b => b.YearType.toString() === selectedYearType);
        if (balance) {
          queryParams.append('accountPeriodBalanceOid', balance.Oid);
        }
      }
      
      const apiUrl = `/api/account-period-balance-item${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const result = await api.get(apiUrl);
      
      if (result?.success && result?.data) {
        setItemData(result.data);
      }
    } catch (error) {
      console.error('❌ Error fetching AccountPeriodBalanceItems:', error);
    }
  }, [selectedYearType, selectedCustomer, accountPeriodBalances]);

  // Handle inline add row
  const handleInlineAdd = () => {
    // Find current year AccountPeriodBalance
    const currentYear = new Date().getFullYear();
    const currentYearBalance = accountPeriodBalances.find(
      balance => balance.YearType === currentYear
    ) || accountPeriodBalances[0] || null;
    
    setNewRow({
      AccountPeriodBalanceOid: currentYearBalance?.Oid || '',
      AccountOid: '',
      ActiveAmount: null,
      PassiveAmount: null,
      BusinessCustomerOid: selectedCustomer !== 'Бүгд' ? selectedCustomer : '',
    });
    setEditingRow('new');
  };

  // Handle save inline row
  const handleSaveInlineRow = async () => {
    if (!newRow) return;
    
    if (!newRow.AccountPeriodBalanceOid || !newRow.BusinessCustomerOid) {
      setErrorMessage('AccountPeriodBalance болон Бизнес харилцагч заавал шаардлагатай');
      setShowErrorPopup(true);
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        AccountPeriodBalanceOid: newRow.AccountPeriodBalanceOid,
        AccountOid: newRow.AccountOid || null,
        ActiveAmount: newRow.ActiveAmount || null,
        PassiveAmount: newRow.PassiveAmount || null,
        BusinessCustomerOid: newRow.BusinessCustomerOid,
      };

      const result = await api.post('/api/account-period-balance-item', submitData);
      
      if (result?.success) {
        setNewRow(null);
        setEditingRow(null);
        fetchAccountPeriodBalanceItems();
      } else {
        throw new Error(result?.message || 'Мэдээлэл нэмэхэд алдаа гарлаа');
      }
    } catch (error: any) {
      console.error('Error saving inline row:', error);
      setErrorMessage(error.response?.data?.message || 'Мэдээлэл нэмэхэд алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel inline add
  const handleCancelInlineAdd = () => {
    setNewRow(null);
    setEditingRow(null);
    setEditingRowData(null);
  };

  // Handle edit row
  const handleEditRow = (row: AccountPeriodBalanceItemRecord) => {
    setEditingRow(row.Oid);
    setEditingRowData({
      AccountPeriodBalanceOid: row.AccountPeriodBalanceOid,
      AccountOid: row.AccountOid || '',
      ActiveAmount: row.ActiveAmount,
      PassiveAmount: row.PassiveAmount,
      BusinessCustomerOid: row.BusinessCustomerOid || '',
    });
  };

  // Handle save edited row
  const handleSaveEditedRow = async () => {
    if (!editingRow || !editingRowData) return;

    try {
      setLoading(true);
      const submitData: any = {};

      if (editingRowData.AccountOid !== undefined) {
        submitData.AccountOid = editingRowData.AccountOid || null;
      }
      if (editingRowData.ActiveAmount !== undefined) {
        submitData.ActiveAmount = editingRowData.ActiveAmount;
      }
      if (editingRowData.PassiveAmount !== undefined) {
        submitData.PassiveAmount = editingRowData.PassiveAmount;
      }
      if (editingRowData.BusinessCustomerOid !== undefined) {
        submitData.BusinessCustomerOid = editingRowData.BusinessCustomerOid && editingRowData.BusinessCustomerOid.trim() !== '' 
          ? editingRowData.BusinessCustomerOid 
          : null;
      }

      const result = await api.put(`/api/account-period-balance-item/${editingRow}`, submitData);
      
      if (result?.success) {
        setEditingRow(null);
        setEditingRowData(null);
        fetchAccountPeriodBalanceItems();
        fetchAccountPeriodBalance(); // Refresh balance to update totals
      } else {
        throw new Error(result?.message || 'Мэдээлэл засахад алдаа гарлаа');
      }
    } catch (error: any) {
      console.error('Error updating row:', error);
      setErrorMessage(error.response?.data?.message || 'Мэдээлэл засахад алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditingRowData(null);
  };

  // Calculate footer data with aggregations
  const calculateFooterData = (): any => {
    if (balanceData.length === 0) {
      return null;
    }

    const sums = {
      activeAmount: 0,
      passiveAmount: 0,
    };

    balanceData.forEach((row) => {
      if (row.ActiveAmount !== null && row.ActiveAmount !== undefined && !isNaN(row.ActiveAmount)) {
        sums.activeAmount += parseFloat(row.ActiveAmount.toString());
      }
      if (row.PassiveAmount !== null && row.PassiveAmount !== undefined && !isNaN(row.PassiveAmount)) {
        sums.passiveAmount += parseFloat(row.PassiveAmount.toString());
      }
    });

    return {
      CustomerPin: `Нийт: ${balanceData.length}`,
      CustomerName: '',
      YearType: '',
      ActiveAmount: sums.activeAmount,
      PassiveAmount: sums.passiveAmount,
    };
  };

  // Create columns for AccountPeriodBalanceItem data
  const getItemColumns = (): SmartTableColumn[] => [
    { 
      key: 'AccountOid', 
      label: 'Данс',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        if (editingRow === row.Oid) {
          return (
            <select
              value={editingRowData?.AccountOid || ''}
              onChange={(e) => {
                if (editingRowData) {
                  setEditingRowData({ ...editingRowData, AccountOid: e.target.value });
                }
              }}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            >
              <option value="">Сонгох...</option>
              {accounts.map((acc) => (
                <option key={acc.Oid} value={acc.Oid}>
                  {acc.Code} - {acc.Name}
                </option>
              ))}
            </select>
          );
        }
        if (editingRow === 'new' && newRow) {
          return (
            <select
              value={newRow.AccountOid || ''}
              onChange={(e) => {
                setNewRow({ ...newRow, AccountOid: e.target.value });
              }}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            >
              <option value="">Сонгох...</option>
              {accounts.map((acc) => (
                <option key={acc.Oid} value={acc.Oid}>
                  {acc.Code} - {acc.Name}
                </option>
              ))}
            </select>
          );
        }
        const account = accounts.find(a => a.Oid === row.AccountOid);
        return account ? `${account.Code} - ${account.Name}` : (row.AccountOid || '-');
      }
    },
    { 
      key: 'ActiveAmount', 
      label: 'Актив',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter && typeof value === 'number') {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{value.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>;
        }
        if (editingRow === row.Oid) {
          return (
            <input
              type="number"
              step="0.01"
              value={editingRowData?.ActiveAmount ?? ''}
              onChange={(e) => {
                if (editingRowData) {
                  setEditingRowData({ 
                    ...editingRowData, 
                    ActiveAmount: e.target.value ? parseFloat(e.target.value) : null 
                  });
                }
              }}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              placeholder="0.00"
            />
          );
        }
        if (editingRow === 'new' && newRow) {
          return (
            <input
              type="number"
              step="0.01"
              value={newRow.ActiveAmount ?? ''}
              onChange={(e) => {
                setNewRow({ ...newRow, ActiveAmount: e.target.value ? parseFloat(e.target.value) : null });
              }}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              placeholder="0.00"
            />
          );
        }
        if (value === null || value === undefined) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '-';
        return numValue.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    },
    { 
      key: 'PassiveAmount', 
      label: 'Пассив',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter && typeof value === 'number') {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{value.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>;
        }
        if (editingRow === row.Oid) {
          return (
            <input
              type="number"
              step="0.01"
              value={editingRowData?.PassiveAmount ?? ''}
              onChange={(e) => {
                if (editingRowData) {
                  setEditingRowData({ 
                    ...editingRowData, 
                    PassiveAmount: e.target.value ? parseFloat(e.target.value) : null 
                  });
                }
              }}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              placeholder="0.00"
            />
          );
        }
        if (editingRow === 'new' && newRow) {
          return (
            <input
              type="number"
              step="0.01"
              value={newRow.PassiveAmount ?? ''}
              onChange={(e) => {
                setNewRow({ ...newRow, PassiveAmount: e.target.value ? parseFloat(e.target.value) : null });
              }}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              placeholder="0.00"
            />
          );
        }
        if (value === null || value === undefined) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '-';
        return numValue.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    },
    { 
      key: 'CustomerName', 
      label: 'Харилцагч',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        if (editingRow === row.Oid) {
          return (
            <select
              value={editingRowData?.BusinessCustomerOid || ''}
              onChange={(e) => {
                if (editingRowData) {
                  setEditingRowData({ ...editingRowData, BusinessCustomerOid: e.target.value });
                }
              }}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            >
              <option value="">Сонгох...</option>
              <optgroup label="Харилцагч">
                {customers.map((customer) => (
                  <option key={customer.customerOid} value={customer.customerOid}>
                    {customer.customerName} ({customer.customerID})
                  </option>
                ))}
              </optgroup>
              <optgroup label="Бизнес харилцагч">
                {businessCustomers.map((bc, index) => (
                  <option key={`bc-${index}`} value={bc.Code}>
                    {bc.Code} - {bc.Name}
                  </option>
                ))}
              </optgroup>
            </select>
          );
        }
        if (editingRow === 'new' && newRow) {
          return (
            <select
              value={newRow.BusinessCustomerOid || ''}
              onChange={(e) => {
                setNewRow({ ...newRow, BusinessCustomerOid: e.target.value });
              }}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            >
              <option value="">Сонгох...</option>
              <optgroup label="Харилцагч">
                {customers.map((customer) => (
                  <option key={customer.customerOid} value={customer.customerOid}>
                    {customer.customerName} ({customer.customerID})
                  </option>
                ))}
              </optgroup>
              <optgroup label="Бизнес харилцагч">
                {businessCustomers.map((bc, index) => (
                  <option key={`bc-${index}`} value={bc.Code}>
                    {bc.Code} - {bc.Name}
                  </option>
                ))}
              </optgroup>
            </select>
          );
        }
        return value || '-';
      }
    },
    {
      key: 'actions',
      label: 'Үйлдэл',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        if (editingRow === row.Oid) {
          return (
            <div className="flex gap-2">
              <button
                onClick={handleSaveEditedRow}
                className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
              >
                Хадгалах
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
              >
                Цуцлах
              </button>
            </div>
          );
        }
        return (
          <button
            onClick={() => handleEditRow(row)}
            className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
          >
            Засах
          </button>
        );
      }
    },
  ];

  // Calculate footer data for items
  const calculateItemFooterData = (): any => {
    if (itemData.length === 0) {
      return null;
    }

    const sums = {
      activeAmount: 0,
      passiveAmount: 0,
    };

    itemData.forEach((row) => {
      if (row.ActiveAmount !== null && row.ActiveAmount !== undefined && !isNaN(row.ActiveAmount)) {
        sums.activeAmount += parseFloat(row.ActiveAmount.toString());
      }
      if (row.PassiveAmount !== null && row.PassiveAmount !== undefined && !isNaN(row.PassiveAmount)) {
        sums.passiveAmount += parseFloat(row.PassiveAmount.toString());
      }
    });

    return {
      AccountOid: `Нийт: ${itemData.length}`,
      ActiveAmount: sums.activeAmount,
      PassiveAmount: sums.passiveAmount,
      CustomerName: '',
    };
  };

  // Create columns for AccountPeriodBalance data
  const getColumns = (): SmartTableColumn[] => [
    { 
      key: 'CustomerPin', 
      label: 'Харилцагчийн регистр',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter && typeof value === 'string' && value.startsWith('Нийт:')) {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{value}</div>;
        }
        return value || '-';
      }
    },
    { 
      key: 'CustomerName', 
      label: 'Харилцагчийн нэр',
      render: (value: any) => value || '-'
    },
    { 
      key: 'YearType', 
      label: 'Жилийн төрөл',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        return value || '-';
      }
    },
    { 
      key: 'ActiveAmount', 
      label: 'Идэвхтэй дүн',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (value === null || value === undefined) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '-';
        const formatted = numValue.toLocaleString('mn-MN', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        });
        if (isFooter) {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{formatted}</div>;
        }
        return formatted;
      }
    },
    { 
      key: 'PassiveAmount', 
      label: 'Хуулийн дүн',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (value === null || value === undefined) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '-';
        const formatted = numValue.toLocaleString('mn-MN', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        });
        if (isFooter) {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{formatted}</div>;
        }
        return formatted;
      }
    },
    {
      key: 'actions',
      label: 'Үйлдэл',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        return (
          <button
            onClick={() => {
              // Open popup with this balance Oid
              setSelectedBalanceOid(row.Oid);
              setShowAddPopup(true);
            }}
            className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
          >
            Засах
          </button>
        );
      }
    },
  ];

  useEffect(() => {
    // Fetch customers list on mount
    fetchCustomers();
    fetchAccounts();
    fetchBusinessCustomers();
  }, [fetchCustomers, fetchAccounts, fetchBusinessCustomers]);

  useEffect(() => {
    // Fetch balance data when filters change
    fetchAccountPeriodBalance();
  }, [fetchAccountPeriodBalance]);

  useEffect(() => {
    // Fetch items data when filters change
    if (accountPeriodBalances.length > 0) {
      fetchAccountPeriodBalanceItems();
    }
  }, [fetchAccountPeriodBalanceItems, accountPeriodBalances.length]);

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Дансны үеийн үлдэгдэл</h1>
          
          {/* Action Buttons */}
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={handleToggleSearch}
            onColumnSettings={handleColumnSettings}
            onRefresh={handleRefresh}
            addable={true}
            onAdd={handleAdd}
          />
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-4">
          <div className="bg-white rounded-lg shadow p-4 min-w-[150px]">
            <div className="text-sm text-gray-600">Нийт бичлэг</div>
            <div className="text-2xl font-bold text-gray-800">
              {balanceData.length}
            </div>
          </div>
          <div className="flex-1 flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Жилийн төрөл сонгох
              </label>
              <select
                value={selectedYearType}
                onChange={(e) => setSelectedYearType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                <option value="">Бүгд</option>
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Харилцагч сонгох
              </label>
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                <option value="Бүгд">Бүгд</option>
                {customers.map((customer) => (
                  <option key={customer.customerOid} value={customer.customerOid}>
                    {customer.customerName} ({customer.customerID})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Уншиж байна...</p>
          </div>
        )}

        {!loading && (
          <>
            <SmartTable
              data={balanceData}
              columns={getColumns()}
              fetchData={fetchAccountPeriodBalance}
              settingsKey="account-period-balance-table"
              editable={false}
              addable={false}
              rowKey="Oid"
              showColumnSearch={showColumnSearch}
              showColumnSettings={showColumnSettings}
              footerData={calculateFooterData()}
            />
            
          </>
        )}

        <ErrorPopup
          isVisible={showErrorPopup}
          message={errorMessage}
          onClose={() => setShowErrorPopup(false)}
        />
        
        <WarningPopup
          isVisible={showWarningAlert}
          message={warningMessage}
          onClose={() => setShowWarningAlert(false)}
          duration={5000}
        />

        <AccountPeriodBalanceItemPopup
          isVisible={showAddPopup}
          onClose={() => {
            setShowAddPopup(false);
            setSelectedBalanceOid(undefined);
          }}
          onSuccess={handleAddSuccess}
          accountPeriodBalanceOid={selectedBalanceOid}
        />
      </div>
    </LayoutWrapper>
  );
};

export default AccountPeriodBalance;